import React from 'react';

export const useUploadProgress = () => {
  const [uploadState, setUploadState] = React.useState<{
    [key: string]: {total: number; loaded: number};
  }>({});

  const trackFileProgress = React.useCallback(
    (file: File) => {
      setUploadState((prevState) => ({
        ...prevState,
        [file.name]: {
          loaded: 0,
          total: file.size,
        },
      }));
    },
    [setUploadState]
  );

  const handleUploadProgress = React.useCallback(
    (filename: string) => (ev: ProgressEvent) =>
      setUploadState((prevState) => ({
        ...prevState,
        [filename]: {
          ...prevState[filename],
          loaded: ev.loaded,
        },
      })),
    [setUploadState]
  );

  const progress = React.useMemo(() => {
    if (Object.keys(uploadState).length === 0) {
      return 100;
    }

    const allFilesState = Object.keys(uploadState).reduce(
      (accumulator, filename) => {
        const fileUploadState = uploadState[filename];
        if (fileUploadState.total === 0) {
          return accumulator;
        }

        return {
          total: accumulator.total + fileUploadState.total,
          loaded: accumulator.loaded + fileUploadState.loaded,
        };
      },
      {total: 0, loaded: 0}
    );

    if (allFilesState.total === 0) {
      return 100;
    }

    return Math.round((allFilesState.loaded / allFilesState.total) * 100);
  }, [uploadState]);

  return {trackFileProgress, handleUploadProgress, progress};
};
